// ============================================================================
// GST ENGAGEMENT LIST SCREEN — G-1 slice 1
// ============================================================================
// Lists GST engagements (gst.tbl_gst_engagement_settings joined to the shared
// prima.tbl_engagement) and opens the setup modal.
//
// The Engagement Type column is shown deliberately: GST owns no engagement
// type, so the type varies per tenant and per engagement, and staff need to
// see which mandate the GST work sits under.
//
// Per prima-web/CLAUDE.md Module Boundaries: GST screens live under
// src/components/modules/gst/ and import only from the shared design system.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  MinimizedModalStrip,
  ColumnSelector,
  type ColumnDefinition,
} from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { gstEngagementService } from 'api/services/gstEngagementService';
import { clientService } from 'api/services/clientService';
import type {
  GstEngagement,
  GstEngagementSearchRequest,
} from 'api/types/gstEngagement.types';
import GstEngagementModal from 'components/modals/modules/gst/GstEngagementModal';
import GstEngagementViewModal from 'components/modals/modules/gst/GstEngagementViewModal';
import GstFilingPeriodsModal from 'components/modals/modules/gst/GstFilingPeriodsModal';
import GstEngagementManageModal from 'components/modals/modules/gst/GstEngagementManageModal';
import GstSourceDocumentsModal from 'components/modals/modules/gst/GstSourceDocumentsModal';
import GstSubmissionsModal from 'components/modals/modules/gst/GstSubmissionsModal';
import GstExpectedSourcesModal from 'components/modals/modules/gst/GstExpectedSourcesModal';
import GstSourceSetupModal from 'components/modals/modules/gst/GstSourceSetupModal';

const DEFAULT_PAGE_SIZE = 25;

type ViewMode = 'list' | 'grid';

const COLUMN_PREF_KEY = 'gst-engagement-list-columns';

const columnDefinitions: ColumnDefinition[] = [
  { id: 'client',            label: 'Client',          field: 'clientLegalName',     required: true,  defaultVisible: true,  sortable: true },
  { id: 'financialYear',     label: 'Financial Year',  field: 'financialYear',       required: true,  defaultVisible: true,  sortable: true },
  { id: 'engagementType',    label: 'Engagement Type', field: 'engagementTypeName',  required: false, defaultVisible: true,  sortable: true },
  { id: 'engagementCode',    label: 'Engagement',      field: 'engagementCode',      required: false, defaultVisible: true,  sortable: true },
  { id: 'registrationCount', label: 'GSTINs',          field: 'registrationCount',   required: false, defaultVisible: true,  sortable: true },
  { id: 'dataSourceMode',    label: 'Data Source',     field: 'dataSourceMode',      required: false, defaultVisible: true,  sortable: false },
];

/** India's financial year starts in April, so before April the "current" FY is
 *  still the previous calendar year. */
const currentFinancialYear = (): number => {
  const now = new Date();
  return now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
};

const fyLabel = (year: number) => `FY ${year}-${String((year + 1) % 100).padStart(2, '0')}`;

interface Props {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  allowedActions?: string[];
}

const GstEngagementListScreen: React.FC<Props> = ({
  moduleIcon,
  tabId,
  moduleName,
  allowedActions = [],
}) => {
  const { isMinimized, getMinimizedModal, dismiss: dismissMinimized } = useModalMinimize();

  const [queryParams, setQueryParams] = useState<GstEngagementSearchRequest>({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const [rows, setRows] = useState<GstEngagement[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('list');

  // Add modal — multi-instance, so several Configure-GST sessions can be
  // parked at once (the blueprint's create pattern).
  const [modalOpen, setModalOpen] = useState(false);
  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  // View modal — one per record.
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<GstEngagement | null>(null);

  // Filing periods — the obligations this engagement owes.
  const [periodsOpen, setPeriodsOpen] = useState(false);
  const [periodsRow, setPeriodsRow] = useState<GstEngagement | null>(null);
  const [manageOpen, setManageOpen] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  const [docsRow, setDocsRow] = useState<GstEngagement | null>(null);
  const [subsOpen, setSubsOpen] = useState(false);
  const [subsRow, setSubsRow] = useState<GstEngagement | null>(null);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [sourcesRow, setSourcesRow] = useState<GstEngagement | null>(null);
  const [setupOpen, setSetupOpen] = useState(false);
  const [setupRow, setSetupRow] = useState<GstEngagement | null>(null);
  const [manageRow, setManageRow] = useState<GstEngagement | null>(null);

  const [visibleColumns, setVisibleColumns] = useState<string[]>(() =>
    columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id),
  );

  useEffect(() => {
    const saved = localStorage.getItem(COLUMN_PREF_KEY);
    if (!saved) return;
    try {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) setVisibleColumns(parsed);
    } catch { /* a corrupt preference just falls back to the defaults */ }
  }, []);

  const handleApplyColumns = useCallback((cols: string[]) => {
    setVisibleColumns(cols);
    localStorage.setItem(COLUMN_PREF_KEY, JSON.stringify(cols));
  }, []);

  const handleResetColumns = useCallback(() => {
    setVisibleColumns(columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id));
    localStorage.removeItem(COLUMN_PREF_KEY);
  }, []);

  // Filter drafts — pushed to queryParams on Apply.
  const [yearFilter, setYearFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [clientOptions, setClientOptions] = useState<{ value: string; label: string }[]>([]);

  const yearOptions = useMemo(() => {
    const base = currentFinancialYear();
    const out = [{ value: '', label: 'All years' }];
    for (let y = base + 1; y > base - 5; y -= 1) out.push({ value: String(y), label: fyLabel(y) });
    return out;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await gstEngagementService.getAll(queryParams);
      setRows(result.data);
      setTotalItems(result.pagination.totalItems);
    } catch (err: any) {
      notificationService.toast.error(
        err?.response?.data?.message ?? 'Could not load GST engagements.',
      );
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => { void load(); }, [load]);

  // Client filter lookup — the picker sends clientId, which is what the
  // backend filters on (GstEngagementQueryParameters.ClientId).
  useEffect(() => {
    clientService
      .getAll({ page: 1, pageSize: 500 } as any)
      .then((r) => {
        setClientOptions([
          { value: '', label: 'All' },
          ...r.data.map((c: any) => ({
            value: String(c.id),
            label: c.legalName?.trim() ? c.legalName : c.name,
          })),
        ]);
      })
      .catch(() => { /* lookup is best-effort — search still covers the client name */ });
  }, []);

  // Search debounce — the backend matches the term across client name,
  // engagement code and engagement type.
  useEffect(() => {
    const timer = setTimeout(() => {
      setQueryParams((prev) => ({ ...prev, searchTerm: searchQuery || undefined, page: 1 }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams((prev) => ({ ...prev, page: pageIndex + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((size: number) => {
    setQueryParams((prev) => ({ ...prev, pageSize: size, page: 1 }));
  }, []);

  const columns: SyutGridColumn<GstEngagement>[] = useMemo(
    () => [
      {
        id: 'client',
        header: 'Client',
        accessorKey: 'clientLegalName',
        size: 240,
        enableSorting: true,
        // Statutory legal name, falling back to the display name only when the
        // client master has none — returns carry the legal name.
        cell: ({ row }) => row.original.clientLegalName || row.original.clientName || '—',
      },
      {
        id: 'financialYear',
        header: 'Financial Year',
        accessorKey: 'financialYear',
        size: 120,
        enableSorting: true,
        cell: ({ row }) => fyLabel(row.original.financialYear),
      },
      {
        id: 'engagementType',
        header: 'Engagement Type',
        accessorKey: 'engagementTypeName',
        size: 170,
        enableSorting: true,
        cell: ({ row }) => row.original.engagementTypeName || '—',
      },
      {
        id: 'engagementCode',
        header: 'Engagement',
        accessorKey: 'engagementCode',
        size: 140,
        enableSorting: true,
        cell: ({ row }) => row.original.engagementCode || '—',
      },
      {
        id: 'registrationCount',
        header: 'GSTINs',
        accessorKey: 'registrationCount',
        size: 90,
        enableSorting: true,
        cell: ({ row }) => row.original.registrationCount ?? 0,
      },
      {
        id: 'dataSourceMode',
        header: 'Data Source',
        accessorKey: 'dataSourceMode',
        size: 150,
        enableSorting: false,
        cell: ({ row }) => row.original.dataSourceMode || '—',
      },
    ],
    [],
  );

  const visibleTableColumns = useMemo<SyutGridColumn<GstEngagement>[]>(
    () => columns.filter((c) => visibleColumns.includes(c.id as string)),
    [columns, visibleColumns],
  );

  // --- Add / View handlers (minimise-aware) ------------------------------
  const handleAddNew = useCallback(() => {
    // An open modal is parked rather than discarded, so starting a second
    // Configure-GST never throws away the first one's work.
    if (modalOpen) formModalMinimizeRef.current?.();
    setCreateInstanceId(String(++createInstanceCounter.current));
    setModalOpen(true);
  }, [modalOpen]);

  const handleView = useCallback((row: GstEngagement) => {
    const viewId = tabId ? `${tabId}-view-modal-${row.id}` : null;
    if (viewId && isMinimized(viewId)) {
      const parked = getMinimizedModal(viewId);
      dismissMinimized(viewId);
      parked?.onRestore?.();
      return;
    }
    setSelectedRow(row);
    setViewOpen(true);
  }, [tabId, isMinimized, getMinimizedModal, dismissMinimized]);

  const handleRestoreModal = useCallback((_mode: 'create', instanceId?: string) => {
    if (instanceId) setCreateInstanceId(instanceId);
    setModalOpen(true);
  }, []);

  const handleRestoreView = useCallback((recordId: string) => {
    const rec = rows.find((r) => r.id === recordId);
    if (rec) setSelectedRow(rec);
    setViewOpen(true);
  }, [rows]);

  const handleFilingPeriods = useCallback((row: GstEngagement) => {
    setPeriodsRow(row);
    setPeriodsOpen(true);
  }, []);

  const handleManage = useCallback((row: GstEngagement) => {
    setManageRow(row);
    setManageOpen(true);
  }, []);

  const handleExpectedSources = useCallback((row: GstEngagement) => {
    setSourcesRow(row);
    setSourcesOpen(true);
  }, []);

  const handleSourceDocuments = useCallback((row: GstEngagement) => {
    setDocsRow(row);
    setDocsOpen(true);
  }, []);

  const handleSubmissions = useCallback((row: GstEngagement) => {
    setSubsRow(row);
    setSubsOpen(true);
  }, []);

  // Manage is offered on `view`, not `edit`: the modal is genuinely useful
  // read-only (scope windows, cadence history, what fell outside scope) and it
  // gates every write on canEdit itself. Hiding it from a preparer would hide
  // the explanation, not just the buttons.
  const getRowActions = useCallback(
    (row: GstEngagement): ActionMenuItem[] => {
      const items: ActionMenuItem[] = [];
      if (allowedActions.length === 0 || allowedActions.includes('view')) {
        items.push({
          id: 'view',
          label: 'View',
          icon: <IconSystem.eye size={16} />,
          onClick: () => handleView(row),
        });
        items.push({
          id: 'periods',
          label: 'Return periods',
          icon: <IconSystem.calendar size={16} />,
          onClick: () => handleFilingPeriods(row),
        });
        items.push({
          id: 'manage',
          label: 'Manage scope & frequency',
          icon: <IconSystem.settings size={16} />,
          onClick: () => handleManage(row),
        });
        items.push({
          id: 'source-setup',
          label: 'Source setup (sample files)',
          icon: <IconSystem.folder size={16} />,
          onClick: () => { setSetupRow(row); setSetupOpen(true); },
        });
        items.push({
          id: 'expected-sources',
          label: 'Expected files',
          icon: <IconSystem.checkCircle size={16} />,
          onClick: () => handleExpectedSources(row),
        });
        items.push({
          id: 'documents',
          label: 'Source documents',
          icon: <IconSystem.upload size={16} />,
          onClick: () => handleSourceDocuments(row),
        });
        items.push({
          id: 'submissions',
          label: 'Submissions',
          icon: <IconSystem.list size={16} />,
          onClick: () => handleSubmissions(row),
        });
      }
      return items;
    },
    [allowedActions, handleView, handleFilingPeriods, handleManage, handleExpectedSources, handleSourceDocuments, handleSubmissions],
  );

  const actionButtons: ActionButton[] = useMemo(
    () => [
      ...(allowedActions.includes('add') ? [{
        id: 'add',
        icon: <IconSystem.add size={18} />,
        label: 'Add',
        tooltip: 'Configure GST for a client',
        onClick: handleAddNew,
        variant: 'icon' as const,
      }] : []),
      {
        id: 'refresh',
        icon: <IconSystem.refresh size={18} />,
        label: 'Refresh',
        tooltip: 'Refresh',
        onClick: () => { void load(); },
        variant: 'icon' as const,
      },
    ],
    [allowedActions, load],
  );

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (queryParams.financialYear !== undefined) count++;
    if (queryParams.clientId) count++;
    return count;
  }, [queryParams.financialYear, queryParams.clientId]);

  const applyFilters = useCallback(() => {
    const yearNum = yearFilter ? Number(yearFilter) : undefined;
    setQueryParams((prev) => ({
      ...prev,
      financialYear: yearNum && !Number.isNaN(yearNum) ? yearNum : undefined,
      clientId: clientFilter || undefined,
      page: 1,
    }));
    setFilterOpen(false);
  }, [yearFilter, clientFilter]);

  const clearFilters = useCallback(() => {
    setYearFilter('');
    setClientFilter('');
    setQueryParams((prev) => ({
      page: 1,
      pageSize: prev.pageSize,
      searchTerm: searchQuery || undefined,
    }));
  }, [searchQuery]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="GST Engagements"
        description="One GST engagement per client per financial year; GSTIN registrations are scoped within it."
        icon={
          moduleIcon && (IconSystem as any)[moduleIcon]
            ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
            : <IconSystem.folder size={18} />
        }
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search client, engagement, type…',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: [
            { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List view' },
            { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid view' },
          ],
          onChange: (mode) => setViewMode(mode as ViewMode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen || activeFilterCount > 0,
          activeFilterCount,
        }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        customToolbarItems={
          <ColumnSelector
            columns={columnDefinitions}
            visibleColumns={visibleColumns}
            onApply={handleApplyColumns}
            onReset={handleResetColumns}
          />
        }
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {viewMode === 'list' && (
              <SyutGrid
                data={rows as any}
                columns={visibleTableColumns as any}
                enableRowSelection
                enablePagination
                manualPagination
                totalCount={totalItems}
                currentPage={(queryParams.page ?? 1) - 1}
                pageSize={queryParams.pageSize ?? DEFAULT_PAGE_SIZE}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                enableSorting
                rowActions={getRowActions as any}
                frozenRightColumns={['actions']}
                emptyMessage={
                  loading
                    ? 'Loading GST engagements…'
                    : 'No GST engagements yet. Click Add to configure one.'
                }
              />
            )}

            {viewMode === 'grid' && (
              <div
                className="syut-grid-component"
                style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
              >
                <div
                  style={{
                    padding: '1rem',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                    gap: '1.5rem',
                    flex: 1,
                    overflow: 'auto',
                    alignContent: 'start',
                  }}
                >
                  {rows.length === 0 && (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                      {loading
                        ? 'Loading GST engagements…'
                        : 'No GST engagements yet. Click Add to configure one.'}
                    </div>
                  )}
                  {rows.map((item) => (
                    <div
                      key={String(item.id)}
                      style={{
                        background: 'var(--bg-primary)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '8px',
                        padding: '1rem',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.5rem',
                        cursor: 'pointer',
                      }}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleView(item)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleView(item);
                        }
                      }}
                    >
                      <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
                        {item.engagementCode || '—'}
                      </div>
                      <div style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                        {item.clientLegalName || item.clientName || '—'}
                      </div>
                      <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                        {item.engagementTypeName || '—'} · {fyLabel(item.financialYear)}
                      </div>
                      <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                        <span>{item.registrationCount ?? 0} GSTIN(s)</span>
                        <span>·</span>
                        <span>{item.dataSourceMode || '—'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Filters cover exactly what the backend filters on
            (GstEngagementQueryParameters: FinancialYear, ClientId). The
            remaining columns are reachable through the header search, which
            the backend matches across client, engagement code and type — a
            filter field the server ignores would lie to the user. */}
        {filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect
                  label="Client"
                  options={clientOptions}
                  value={clientFilter}
                  onChange={(val) => setClientFilter((val as string) || '')}
                  isClearable={false}
                  size="sm"
                />
              </div>
              <div className="filter-section">
                <SyutSelect
                  label="Financial Year"
                  options={yearOptions}
                  value={yearFilter}
                  onChange={(val) => setYearFilter((val as string) || '')}
                  isClearable={false}
                  size="sm"
                />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )}
      </div>

      <GstEngagementModal
        isOpen={modalOpen}
        tabId={tabId}
        moduleName={moduleName}
        createInstanceId={createInstanceId}
        minimizeRef={formModalMinimizeRef}
        onRestoreModal={handleRestoreModal}
        onClose={() => setModalOpen(false)}
        onSaved={() => {
          setModalOpen(false);
          void load();
        }}
      />

      <GstEngagementManageModal
        isOpen={manageOpen}
        engagement={manageRow}
        canEdit={allowedActions.length === 0 || allowedActions.includes('edit')}
        onClose={() => setManageOpen(false)}
        onSaved={() => { void load(); }}
      />

      <GstSourceDocumentsModal
        isOpen={docsOpen}
        engagement={docsRow}
        // Receiving a file is a write and follows `edit`; the list itself is
        // readable on `view`, since what the client sent is part of the record.
        canUpload={allowedActions.length === 0 || allowedActions.includes('edit')}
        canApprove={allowedActions.length === 0 || allowedActions.includes('approve')}
        onClose={() => setDocsOpen(false)}
      />

      <GstSourceSetupModal
        isOpen={setupOpen}
        engagement={setupRow}
        canEdit={allowedActions.length === 0 || allowedActions.includes('edit')}
        canApprove={allowedActions.length === 0 || allowedActions.includes('approve')}
        onClose={() => setSetupOpen(false)}
      />

      <GstExpectedSourcesModal
        isOpen={sourcesOpen}
        engagement={sourcesRow}
        canEdit={allowedActions.length === 0 || allowedActions.includes('edit')}
        onClose={() => setSourcesOpen(false)}
      />

      <GstSubmissionsModal
        isOpen={subsOpen}
        engagement={subsRow}
        canEdit={allowedActions.length === 0 || allowedActions.includes('edit')}
        canApprove={allowedActions.length === 0 || allowedActions.includes('approve')}
        onClose={() => setSubsOpen(false)}
      />

      <GstFilingPeriodsModal
        isOpen={periodsOpen}
        engagement={periodsRow}
        // Generating is a write, so it follows the `edit` grant rather than
        // `view` — reading the calendar and creating obligations are not the
        // same authority.
        canGenerate={allowedActions.length === 0 || allowedActions.includes('edit')}
        onClose={() => setPeriodsOpen(false)}
      />

      <GstEngagementViewModal
        isOpen={viewOpen}
        summary={selectedRow}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreView={handleRestoreView}
        onClose={() => setViewOpen(false)}
      />
    </div>
  );
};

export default GstEngagementListScreen;
